import CommentWidget from '@/widgets/Comment';
import { IProjectDetail } from '@/widgets/Comment/models';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { StatusName } from '@/pages/project-management/detail/constant';
import { renderTaskStatus } from '@/pages/project-management/detail/utils/helper';

import AppTooltip from '@/components/app-tooltip/AppTooltip';
import { TagTaskType } from '@/components/common/task/TagTaskType';

import { CATEGORY } from '@/utils/constants';
import { FORMAT_TIME } from '@/utils/constants/AppConstants';
import { DataViewer } from '@/utils/helpers/common';

import { IDetailTaskData } from '../../models';
import DetailTaskInternal from './DetailTaskInternal';
import HeaderTaskDetail from './Header';
import { TaskDetailQuickUpdateForm } from './TaskDetailQuickUpdateForm';

interface HeaderInternalRoleProps {
  detailData: IDetailTaskData;
  headerRef: React.RefObject<HTMLDivElement>;
  project: IProjectDetail | null;
  fetchData: () => Promise<void>;
  setHasComment?: Dispatch<SetStateAction<boolean>>;
  idProject?: string | number;
  idTask?: string | number;
  form: any;
}

export const ExportPdfComponents = (props: HeaderInternalRoleProps) => {
  const { detailData, headerRef, project, fetchData, setHasComment, idProject, idTask } = props;
  const { t } = useTranslation();

  const getPicName = () => {
    let taskPicks = '';
    for (const key in detailData?.projectTaskPics) {
      const pic = detailData?.projectTaskPics[key];

      if (pic.picStatus) taskPicks += pic.picName;
      else taskPicks += `(${t('account_list:status_account:inactive')}) ${pic.picName}`;

      if (Number(key) !== detailData?.projectTaskPics?.length - 1) taskPicks += '、';
    }
    return taskPicks;
  };

  return (
    <FormProvider {...props.form}>
      <div className='flex flex-col gap-4 title-comment'>
        <div className='pdf-page'>
          <div className='p-5 bg-[#f5f5f7]'>
            <HeaderTaskDetail
              ref={headerRef}
              detailData={{
                ...project,
                ...detailData
              }}
              fetchData={fetchData}
            />
            <div
              className={
                'flex flex-col px-[20px] py-[12px] my-3 bg-white gap-[12px] border border-gray2 rounded-[8px] view-task-detail-project__content'
              }
            >
              <div className='flex justify-between gap-2'>
                <TaskDetailQuickUpdateForm detailData={detailData} fetchData={fetchData} />
              </div>
              <DetailTaskInternal detailData={detailData} compact />
            </div>
          </div>
        </div>

        <div className='pdf-page'>
          <div className='p-5 bg-[#f5f5f7]'>
            <div className='flex items-center gap-4'>
              <TagTaskType type={detailData?.taskType ?? ''} />
              <AppTooltip className='truncate inline-block w-full' title={DataViewer.display(detailData?.taskName)}>
                <div className='text-[24px] font-bold truncate inline-block w-full'>{DataViewer.display(detailData?.taskName)}</div>
              </AppTooltip>
              <div className='flex items-center gap-10'>
                <div className='flex whitespace-nowrap truncate items-center'>
                  <span>{t('project_detail:task_list:deadline')}：</span>
                  <span>
                    {detailData?.deadline ? dayjs(DataViewer.display(detailData?.deadline)).format('YYYY/MM/DD') : '--'}
                    {detailData?.deadlineTime ? `\u3000${dayjs(detailData.deadlineTime, 'HH:mm').format(FORMAT_TIME)}` : ''}
                  </span>
                </div>
                <div className='flex whitespace-nowrap truncate items-center'>
                  <span>{t('project:task_of_project:mot_pick:label')}：</span>
                  <span>{getPicName()?.length > 25 ? DataViewer.display(`${getPicName().slice(0, 25)}...`) : DataViewer.display(getPicName())}</span>
                </div>
                <div>{renderTaskStatus(detailData?.statusName as StatusName, '!w-40 !inline-flex !px-[8px] !h-[24px] my-auto')}</div>
              </div>
            </div>
            <div className='text-lnk font-normal'>
              {t('sider:project_details')}: {project?.code}
            </div>
            <CommentWidget.Container
              projectId={String(idProject)}
              projectProcessId={detailData?.projectProcessId}
              taskId={String(idTask)}
              taskType={detailData?.taskType ?? CATEGORY.CS}
              setHasComment={setHasComment}
              className='from-container-chat mt-4'
            />
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
